import { useEffect, useId, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Input } from './Input.js';
import { Label } from './Label.js';
import { cn } from '../lib/utils.js';
import { withinBounds, type DayKey } from '../lib/dayKey.js';
import { formatDayFirst, parseDayFirst } from '../lib/dayKeyText.js';
import { failureMessage, type DateFieldLabels } from './dateLabels.js';

/* ===========================================================================
 * TYPING A DATE — the half of a date picker that a calendar cannot replace.
 *
 * Clicking back through eleven months to reach a passport expiry is not a
 * control. So every popover in this set opens with a real text field on it,
 * day-first, and the grid below is the SHORTCUT rather than the only road.
 *
 * WHEN IT COMMITS. On blur and on Enter, never on every keystroke. Validating
 * mid-word means the reader is told "September has 30 days" while they are
 * still typing the 3 of 31 — an error that is technically true and practically
 * an accusation. Typing therefore clears the message; leaving the field states
 * it.
 *
 * WHAT IT SAYS. `31-09-2026` reports "September has 30 days.", not "invalid":
 * the reader can fix a named problem in one keystroke and can only guess at an
 * unnamed one (design-rules §3.10, §3.11). The sentence itself comes from the
 * caller's labels — this package has no i18n.
 * ======================================================================== */

export interface DayInputProps {
  /** The day key in force, or ''. */
  value: string;
  onCommit: (key: DayKey) => void;
  /** Called when the reader empties the field, where a caller allows it. */
  onClear?: () => void;
  labels: DateFieldLabels;
  label: string;
  min?: DayKey | null;
  max?: DayKey | null;
  autoFocus?: boolean;
  className?: string;
}

export function DayInput({
  value,
  onCommit,
  onClear,
  labels,
  label,
  min,
  max,
  autoFocus,
  className,
}: DayInputProps): JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  const [text, setText] = useState(() => formatDayFirst(value));
  const [error, setError] = useState<string | null>(null);

  /* The field follows the VALUE while the reader is not the one changing it —
     picking 12 September in the grid has to update the box that says what is
     chosen, or the screen states one date in two places (§3.2). */
  useEffect(() => {
    setText(formatDayFirst(value));
    setError(null);
  }, [value]);

  function commit(): void {
    const parsed = parseDayFirst(text);
    if (!parsed.ok) {
      if (parsed.failure.kind === 'empty') {
        setError(null);
        /* ONLY WHEN THERE WAS SOMETHING TO CLEAR.
         *
         * `commit` also runs on BLUR, and one blur is not the reader's doing:
         * picking a day in the grid calls `onChange` and then closes the
         * popover, which takes focus off this box on its way out. On a field
         * that opened EMPTY the text is still empty at that instant, and an
         * unconditional `onClear()` read that as "the reader emptied the
         * field" — throwing away the day they had just clicked.
         *
         * It was not a cosmetic loss: `onClear` is a way OUT of the control,
         * not just an empty value (`AnnouncementFields` puts its "Set an end
         * date" button back there), so choosing a date made the whole field
         * disappear.
         *
         * An empty box over an empty value is nothing to report. The real
         * clear — a reader deleting a day that WAS set — still has
         * `value !== ''` and still fires, which the second case in
         * `DateField.emptyBlurClear.test.tsx` pins. */
        if (value !== '') onClear?.();
        return;
      }
      setError(failureMessage(parsed.failure, labels));
      return;
    }
    if (!withinBounds(parsed.key, min, max)) {
      setError(labels.outOfBounds(formatDayFirst(min) || '—', formatDayFirst(max) || '—'));
      return;
    }
    setError(null);
    /* Only when it CHANGED. `commit` also runs on blur, and blur happens on the
       way out of the popover — an unconditional call would fire `onChange` with
       the value the field already held every time the reader pressed Escape,
       which turns a cancel into a save. */
    if (parsed.key !== value) onCommit(parsed.key);
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'Enter') return;
    // Otherwise the surrounding form submits with a date the popover has not
    // yet accepted.
    e.preventDefault();
    commit();
  }

  return (
    <div className={cn('space-y-tight', className)}>
      <Label htmlFor={id} className="text-label text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        // `text`, not `date`: the whole point is that the app draws the order.
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        value={text}
        placeholder={labels.placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {error && (
        <p id={errorId} role="alert" className="text-copy-sm text-danger-on-surface">
          {error}
        </p>
      )}
    </div>
  );
}
